import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  CalendarCheck2,
  ClipboardList,
  Download,
  Filter,
  Package,
  ReceiptText,
  RotateCcw,
  Search,
  TrendingDown,
  TrendingUp,
  Users,
  WalletCards,
} from 'lucide-react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useApiClient } from '../lib/apiClient';
import { formatMoney } from '../i18n/orgFormat';
import { formatInventoryMeasurement } from '../lib/inventoryQuantityFormatting';

type Option = { id: string; name: string };
type ProviderOption = {
  id: string;
  userId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  linkedUser?: { id: string; firstName?: string | null; lastName?: string | null } | null;
};

type OperationsReport = {
  range: { start: string; end: string; locationIds: string[] };
  filters: {
    scope: 'default' | 'all';
    locationId: string | null;
    providerId: string | null;
    appointmentTypeId: string | null;
  };
  summary: Record<string, number>;
  topServices: Array<{ appointmentTypeId: string; serviceName: string; count: number }>;
  leastUsedServices: Array<{ appointmentTypeId: string; serviceName: string; count: number; lastUsedAt?: string | null }>;
  appointmentTypeCounts: Array<{ appointmentTypeId: string; appointmentTypeName: string; count: number }>;
  topInventoryItems: Array<{ itemId: string; itemName: string; quantity: number; unitLabel?: string | null }>;
  leastUsedInventoryItems: Array<{ itemId: string; itemName: string; quantity: number; unitLabel?: string | null; lastUsedAt?: string | null }>;
  stockReceiptVsUsage: {
    receipts: number;
    usage: number;
    receiptsByUnit?: Array<{ unitLabel: string; quantity: number }>;
    usageByUnit?: Array<{ unitLabel: string; quantity: number }>;
  };
  financialSummary: {
    revenue: number;
    discounts: number;
    invoiceCount: number;
    averageTicket: number;
  } | null;
  topRevenueServices: Array<{ appointmentTypeId: string; serviceName: string; invoiceCount: number; revenue: number }>;
};

type PeriodPreset = 'week' | 'month' | 'custom';
type ExportSection = 'all' | 'summary' | 'appointments' | 'services' | 'inventory' | 'revenue';
type InsightTone = 'good' | 'watch' | 'risk' | 'neutral';

const chartPalette = ['#0f172a', '#0ea5e9', '#10b981', '#f59e0b', '#fb7185', '#8b5cf6', '#14b8a6', '#6366f1'];
const chartInk = '#0f172a';
const chartAccent = '#0ea5e9';
const chartSuccess = '#10b981';
const chartWarning = '#f59e0b';
const chartDanger = '#fb7185';
const chartPackage = '#8b5cf6';
const axisLabelStyle = { color: '#64748b', fontSize: '11px' };

function formatCurrency(value: number) {
  return formatMoney(value, { maximumFractionDigits: 0 });
}

function formatNumber(value: number | null | undefined) {
  return Number(value || 0).toLocaleString();
}

function formatPercent(value: number) {
  if (!Number.isFinite(value)) return '0%';
  return `${Math.round(value)}%`;
}

function formatShortDate(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function safeRate(numerator: number, denominator: number) {
  if (!denominator) return 0;
  return (numerator / denominator) * 100;
}

function currentThemeIsDark() {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute('data-theme') === 'dark';
}

function chartBase(_title: string, dark: boolean): Highcharts.Options {
  const textColor = dark ? '#e3e5e8' : '#0f172a';
  const mutedColor = dark ? '#8e9297' : '#64748b';
  const borderColor = dark ? '#2c2f33' : '#dbe3eb';
  const gridColor = dark ? '#252a30' : '#e8edf3';
  const tooltipBackground = dark ? '#111827' : '#ffffff';
  return {
    chart: {
      backgroundColor: 'transparent',
      style: { fontFamily: 'inherit' },
      spacing: [8, 8, 10, 8],
    },
    title: { text: undefined },
    colors: chartPalette,
    credits: { enabled: false },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      symbolHeight: 8,
      symbolWidth: 8,
      symbolRadius: 4,
      itemStyle: { color: mutedColor, fontWeight: '400', fontSize: '12px' },
      itemHoverStyle: { color: textColor },
    },
    xAxis: {
      labels: { style: axisLabelStyle },
      lineColor: borderColor,
      tickColor: borderColor,
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: gridColor,
      labels: { style: axisLabelStyle },
    },
    tooltip: {
      shared: true,
      backgroundColor: tooltipBackground,
      borderRadius: 12,
      borderColor,
      shadow: true,
      style: { color: textColor, fontSize: '12px' },
    },
  };
}

function escapeCsvCell(value: string | number | null | undefined) {
  const normalized = String(value ?? '');
  if (!/[",\n]/.test(normalized)) return normalized;
  return `"${normalized.replace(/"/g, '""')}"`;
}

function downloadCsv(filename: string, rows: Array<Array<string | number | null | undefined>>) {
  const csv = rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function toDateInputValue(date: Date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function startOfWeek(date: Date) {
  const value = new Date(date);
  const day = value.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  value.setDate(value.getDate() + diff);
  value.setHours(0, 0, 0, 0);
  return value;
}

function buildPresetRange(preset: Exclude<PeriodPreset, 'custom'>) {
  const now = new Date();
  if (preset === 'week') {
    return { startDate: toDateInputValue(startOfWeek(now)), endDate: toDateInputValue(now) };
  }
  return { startDate: toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: toDateInputValue(now) };
}

function providerLabel(provider: ProviderOption) {
  const firstName = provider.linkedUser?.firstName || provider.firstName || '';
  const lastName = provider.linkedUser?.lastName || provider.lastName || '';
  return `${firstName} ${lastName}`.trim() || provider.userId || provider.id;
}

function normalizeOperationsReport(payload: OperationsReport): OperationsReport {
  return {
    ...payload,
    range: payload.range ?? { start: '', end: '', locationIds: [] },
    filters: payload.filters ?? { scope: 'default', locationId: null, providerId: null, appointmentTypeId: null },
    summary: payload.summary ?? {},
    topServices: Array.isArray(payload.topServices) ? payload.topServices : [],
    leastUsedServices: Array.isArray(payload.leastUsedServices) ? payload.leastUsedServices : [],
    appointmentTypeCounts: Array.isArray(payload.appointmentTypeCounts) ? payload.appointmentTypeCounts : [],
    topInventoryItems: Array.isArray(payload.topInventoryItems) ? payload.topInventoryItems : [],
    leastUsedInventoryItems: Array.isArray(payload.leastUsedInventoryItems) ? payload.leastUsedInventoryItems : [],
    stockReceiptVsUsage: payload.stockReceiptVsUsage ?? { receipts: 0, usage: 0, receiptsByUnit: [], usageByUnit: [] },
    financialSummary: payload.financialSummary ?? null,
    topRevenueServices: Array.isArray(payload.topRevenueServices) ? payload.topRevenueServices : [],
  };
}

function namedFilter(id: string | null | undefined, options: Option[], fallback: string) {
  if (!id) return fallback;
  return options.find((option) => option.id === id)?.name || fallback;
}

export default function OperationalReportsPage() {
  const { fetchJson } = useApiClient();
  const [data, setData] = useState<OperationsReport | null>(null);
  const [providers, setProviders] = useState<Option[]>([]);
  const [services, setServices] = useState<Option[]>([]);
  const [locations, setLocations] = useState<Option[]>([]);
  const [scope, setScope] = useState<'default' | 'all'>('default');
  const [locationId, setLocationId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [appointmentTypeId, setAppointmentTypeId] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => toDateInputValue(new Date()));
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('month');
  const [exportSection, setExportSection] = useState<ExportSection>('all');
  const [reportCategory, setReportCategory] = useState('All reports');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const [providerPayload, servicePayload, locationPayload] = await Promise.all([
          fetchJson<ProviderOption[]>('/providers'),
          fetchJson<Array<{ id: string; name: string }>>('/appointment-types'),
          fetchJson<Array<{ id: string; name: string }>>('/locations'),
        ]);
        if (!active) return;
        setProviders(
          providerPayload
            .filter((provider) => Boolean(provider.userId || provider.linkedUser?.id))
            .map((provider) => ({ id: provider.userId || provider.linkedUser?.id || provider.id, name: providerLabel(provider) })),
        );
        setServices(servicePayload.map((service) => ({ id: service.id, name: service.name })));
        setLocations(locationPayload.map((location) => ({ id: location.id, name: location.name })));
      } catch {
        if (!active) return;
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchJson]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ scope });
      if (locationId) params.set('locationId', locationId);
      if (providerId) params.set('providerId', providerId);
      if (appointmentTypeId) params.set('appointmentTypeId', appointmentTypeId);
      if (startDate) params.set('start', new Date(startDate).toISOString());
      if (endDate) {
        const inclusiveEnd = new Date(endDate);
        inclusiveEnd.setDate(inclusiveEnd.getDate() + 1);
        params.set('end', inclusiveEnd.toISOString());
      }
      const payload = await fetchJson<OperationsReport>(`/reports/operations?${params.toString()}`);
      setData(normalizeOperationsReport(payload));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load operational reports.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [scope, locationId, providerId, appointmentTypeId, startDate, endDate]);

  function applyPreset(preset: Exclude<PeriodPreset, 'custom'>) {
    const range = buildPresetRange(preset);
    setPeriodPreset(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  }

  function exportCurrentReport(section: ExportSection = exportSection) {
    if (!data) return;
    const baseRows: Array<Array<string | number | null | undefined>> = [
      ['Operations overview export'],
      ['Start', data.range.start],
      ['End', data.range.end],
      ['Scope', data.filters.scope],
      ['Location filter', data.filters.locationId || 'Any location in scope'],
      ['Provider filter', data.filters.providerId || 'All providers'],
      ['Service filter', data.filters.appointmentTypeId || 'All services'],
    ];

    const rows: Array<Array<string | number | null | undefined>> = [...baseRows];

    const appendSummary = () => {
      rows.push(
        [],
        ['Summary metric', 'Value'],
        ['appointments', data.summary.appointments],
        ['completed appointments', data.summary.completedServices],
        ['invoices created', data.summary.salesRegistered],
        ['no shows', data.summary.noShows],
        ['cancellations', data.summary.cancellations],
        ['package redemptions', data.summary.packageRedemptions],
        ['returning patients', data.summary.repeatPatients],
        ['low stock items', data.summary.lowStockItems],
      );
    };

    const appendRevenue = () => {
      if (data.financialSummary) {
        rows.push(
          [],
          ['Financial summary', 'Value'],
          ['revenue', data.financialSummary.revenue],
          ['discounts', data.financialSummary.discounts],
          ['invoiceCount', data.financialSummary.invoiceCount],
          ['averageTicket', data.financialSummary.averageTicket],
        );
      }
      rows.push(
        [],
        ['Top revenue services'],
        ['Service', 'Invoices', 'Revenue'],
        ...data.topRevenueServices.map((row) => [row.serviceName, row.invoiceCount, row.revenue]),
      );
    };

    const appendSection = (title: string, header: string[], items: Array<Array<string | number>>) => {
      rows.push([], [title], header, ...items);
    };

    if (section === 'all' || section === 'summary') appendSummary();

    if (section === 'all' || section === 'appointments') {
      appendSection('Appointment types', ['Appointment type', 'Count'], data.appointmentTypeCounts.map((row) => [row.appointmentTypeName, row.count]));
      rows.push([], ['Appointment outcomes'], ['Metric', 'Value'], ['Completed appointments', data.summary.completedServices], ['No-shows', data.summary.noShows], ['Cancellations', data.summary.cancellations], ['Package redemptions', data.summary.packageRedemptions]);
    }

    if (section === 'all' || section === 'services') {
      appendSection('Top services', ['Service', 'Count'], data.topServices.map((row) => [row.serviceName, row.count]));
      appendSection('Least used services', ['Service', 'Count'], data.leastUsedServices.map((row) => [row.serviceName, row.count]));
    }

    if (section === 'all' || section === 'inventory') {
      appendSection('Top inventory items used', ['Item', 'Base quantity', 'Base UOM'], data.topInventoryItems.map((row) => [row.itemName, row.quantity, row.unitLabel || 'unit']));
      appendSection('Least used inventory items', ['Item', 'Base quantity', 'Base UOM'], data.leastUsedInventoryItems.map((row) => [row.itemName, row.quantity, row.unitLabel || 'unit']));
      appendSection('Stock receipts by entered UOM', ['Unit', 'Quantity'], (data.stockReceiptVsUsage.receiptsByUnit || []).map((row) => [row.unitLabel, row.quantity]));
      appendSection('Stock usage by entered UOM', ['Unit', 'Quantity'], (data.stockReceiptVsUsage.usageByUnit || []).map((row) => [row.unitLabel, row.quantity]));
    }

    if (section === 'all' || section === 'revenue') appendRevenue();

    downloadCsv(`operations-${section}-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  }

  const summary = data?.summary || {};
  const financialSummary = data?.financialSummary;
  const topServices = data?.topServices ?? [];
  const appointmentTypeCounts = data?.appointmentTypeCounts ?? [];
  const topRevenueServices = data?.topRevenueServices ?? [];
  const stockReceiptVsUsage = data?.stockReceiptVsUsage ?? { receipts: 0, usage: 0, receiptsByUnit: [], usageByUnit: [] };
  const stockUnitLabels = Array.from(new Set([
    ...(stockReceiptVsUsage.receiptsByUnit || []).map((row) => row.unitLabel || 'unit'),
    ...(stockReceiptVsUsage.usageByUnit || []).map((row) => row.unitLabel || 'unit'),
  ]));
  const appointments = Number(summary.appointments || 0);
  const completedAppointments = Number(summary.completedServices || 0);
  const noShows = Number(summary.noShows || 0);
  const cancellations = Number(summary.cancellations || 0);
  const invoiceCount = Number(financialSummary?.invoiceCount || summary.salesRegistered || 0);
  const completionRate = safeRate(completedAppointments, appointments);
  const noShowRate = safeRate(noShows, appointments);
  const cancellationRate = safeRate(cancellations, appointments);
  const invoiceCaptureRate = safeRate(invoiceCount, completedAppointments);
  const dark = currentThemeIsDark();

  const rangeLabel = data
    ? `${new Date(data.range.start).toLocaleDateString()} to ${new Date(data.range.end).toLocaleDateString()}`
    : `${startDate} to ${endDate}`;
  const activeFilterChips = useMemo(() => [
    scope === 'all' ? 'All accessible locations' : 'Only my assigned locations',
    namedFilter(locationId, locations, 'Any location in scope'),
    namedFilter(providerId, providers, 'All doctors/providers'),
    namedFilter(appointmentTypeId, services, 'All services'),
  ], [appointmentTypeId, locationId, locations, providerId, providers, scope, services]);

  const insights = useMemo(() => {
    const rows: Array<{ title: string; detail: string; tone: InsightTone; icon: ReactNode }> = [];
    if (!data) return rows;
    if (appointments === 0) {
      rows.push({
        title: 'No activity in this range',
        detail: 'Broaden the date range or remove filters to review operational trends.',
        tone: 'neutral',
        icon: <Search size={18} />,
      });
      return rows;
    }
    if (noShowRate >= 10) {
      rows.push({
        title: 'No-show rate needs attention',
        detail: `${formatPercent(noShowRate)} of booked visits are no-shows in this filtered period.`,
        tone: 'risk',
        icon: <AlertTriangle size={18} />,
      });
    }
    if (cancellationRate >= 12) {
      rows.push({
        title: 'Cancellations are elevated',
        detail: `${formatPercent(cancellationRate)} of bookings were cancelled. Check services, providers, or reminders.`,
        tone: 'watch',
        icon: <TrendingDown size={18} />,
      });
    }
    if (Number(summary.lowStockItems || 0) > 0) {
      rows.push({
        title: 'Stock follow-up needed',
        detail: `${formatNumber(summary.lowStockItems)} item${Number(summary.lowStockItems) === 1 ? '' : 's'} below reorder point.`,
        tone: 'watch',
        icon: <Package size={18} />,
      });
    }
    if (financialSummary && invoiceCaptureRate < 90 && completedAppointments > 0) {
      rows.push({
        title: 'Billing capture gap',
        detail: `${formatPercent(invoiceCaptureRate)} invoice capture against completed appointments.`,
        tone: 'risk',
        icon: <ReceiptText size={18} />,
      });
    }
    if (!rows.length) {
      rows.push({
        title: 'Operations look stable',
        detail: 'Completion, billing, and stock indicators are within normal range for the selected filters.',
        tone: 'good',
        icon: <TrendingUp size={18} />,
      });
    }
    return rows.slice(0, 4);
  }, [appointments, cancellationRate, completedAppointments, data, financialSummary, invoiceCaptureRate, noShowRate, summary.lowStockItems]);

  const appointmentOutcomeOptions: Highcharts.Options = {
    ...chartBase('Visit outcomes', dark),
    chart: { ...(chartBase('Visit outcomes', dark).chart || {}), type: 'column', height: 280 },
    legend: { enabled: false },
    xAxis: {
      ...(chartBase('Visit outcomes', dark).xAxis as Highcharts.XAxisOptions),
      categories: ['Completed', 'No-shows', 'Cancellations', 'Package redemptions'],
    },
    plotOptions: {
      column: { borderRadius: 8, pointPadding: 0.14, groupPadding: 0.12 },
    },
    series: [{
      type: 'column',
      name: 'Count',
      colorByPoint: true,
      data: [
        { y: completedAppointments, color: chartInk },
        { y: noShows, color: chartWarning },
        { y: cancellations, color: chartDanger },
        { y: Number(summary.packageRedemptions || 0), color: chartPackage },
      ],
    }],
  };
  const topServiceDemandOptions: Highcharts.Options = {
    ...chartBase('Services delivered or booked', dark),
    chart: { ...(chartBase('Services delivered or booked', dark).chart || {}), type: 'bar', height: 320 },
    legend: { enabled: false },
    xAxis: {
      ...(chartBase('Services delivered or booked', dark).xAxis as Highcharts.XAxisOptions),
      categories: topServices.slice(0, 8).map((row) => row.serviceName),
    },
    plotOptions: {
      bar: { borderRadius: 5, pointPadding: 0.08, groupPadding: 0.08 },
    },
    series: [{ type: 'bar', name: 'Recorded services', data: topServices.slice(0, 8).map((row) => row.count), color: chartInk }],
  };
  const appointmentMixOptions: Highcharts.Options = {
    ...chartBase('Booked appointment types', dark),
    chart: { ...(chartBase('Booked appointment types', dark).chart || {}), type: 'bar', height: 320 },
    legend: { enabled: false },
    xAxis: {
      ...(chartBase('Booked appointment types', dark).xAxis as Highcharts.XAxisOptions),
      categories: appointmentTypeCounts.slice(0, 8).map((row) => row.appointmentTypeName),
    },
    plotOptions: {
      bar: { borderRadius: 5, pointPadding: 0.08, groupPadding: 0.08 },
    },
    series: [{ type: 'bar', name: 'Appointments', data: appointmentTypeCounts.slice(0, 8).map((row) => row.count), color: chartInk }],
  };
  const revenueByServiceOptions: Highcharts.Options = {
    ...chartBase(financialSummary ? 'Top revenue services' : 'Stock receipts vs usage', dark),
    chart: { ...(chartBase('Revenue services', dark).chart || {}), type: financialSummary ? 'bar' : 'column', height: 320 },
    legend: { enabled: !financialSummary },
    xAxis: {
      ...(chartBase('Revenue services', dark).xAxis as Highcharts.XAxisOptions),
      categories: financialSummary ? topRevenueServices.slice(0, 8).map((row) => row.serviceName) : stockUnitLabels,
    },
    plotOptions: {
      bar: { borderRadius: 5, pointPadding: 0.08, groupPadding: 0.08 },
      column: { borderRadius: 8, pointPadding: 0.14 },
    },
    tooltip: {
      ...chartBase('Revenue services', dark).tooltip,
      pointFormatter: financialSummary
        ? function () { return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${formatCurrency(Number(this.y || 0))}</b><br/>`; }
        : undefined,
    },
    series: financialSummary
      ? [{ type: 'bar', name: 'Revenue', data: topRevenueServices.slice(0, 8).map((row) => row.revenue), color: chartAccent }]
      : [
          {
            type: 'column',
            name: 'Receipts',
            data: stockUnitLabels.map((unitLabel) => (stockReceiptVsUsage.receiptsByUnit || []).find((row) => row.unitLabel === unitLabel)?.quantity || 0),
            color: chartAccent,
            dataLabels: { enabled: true },
          },
          {
            type: 'column',
            name: 'Usage',
            data: stockUnitLabels.map((unitLabel) => (stockReceiptVsUsage.usageByUnit || []).find((row) => row.unitLabel === unitLabel)?.quantity || 0),
            color: chartSuccess,
            dataLabels: { enabled: true },
          },
        ],
  };

  return (
    <div className="min-h-full bg-slate-50/60 px-5 py-6 lg:px-8">
      <main className="mx-auto max-w-[1500px] space-y-5">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Operational reports</p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">Operations overview</h1>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Appointments show booked visit types. Services show recorded treatment names and fall back to the booked service when no completion is recorded.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/reports/financial" className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50">
                <WalletCards size={16} />
                Financial overview
              </Link>
              <select value={exportSection} onChange={(event) => setExportSection(event.target.value as ExportSection)} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm">
                <option value="all">Export all filtered data</option>
                <option value="summary">Export summary only</option>
                <option value="appointments">Export appointments only</option>
                <option value="services">Export services only</option>
                <option value="inventory">Export inventory only</option>
                <option value="revenue">Export revenue only</option>
              </select>
              <button type="button" onClick={() => exportCurrentReport()} disabled={!data} className="inline-flex h-10 items-center gap-2 rounded-xl bg-slate-950 px-3 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50">
                <Download size={16} />
                Export selected CSV
              </button>
            </div>
          </div>

          <div className="mt-5 grid gap-3 lg:grid-cols-[auto_auto_1fr] lg:items-center">
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
              <button
                type="button"
                onClick={() => applyPreset('week')}
                className={`h-9 rounded-lg px-3 text-sm font-medium transition ${periodPreset === 'week' ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
              >
                This week
              </button>
              <button
                type="button"
                onClick={() => applyPreset('month')}
                className={`h-9 rounded-lg px-3 text-sm font-medium transition ${periodPreset === 'month' ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
              >
                This month
              </button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                type="date"
                value={startDate}
                onChange={(event) => {
                  setPeriodPreset('custom');
                  setStartDate(event.target.value);
                }}
                className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm shadow-sm"
                aria-label="Start date"
              />
              <input
                type="date"
                value={endDate}
                onChange={(event) => {
                  setPeriodPreset('custom');
                  setEndDate(event.target.value);
                }}
                className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm shadow-sm"
                aria-label="End date"
              />
            </div>
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_auto]">
              <select value={locationId} onChange={(event) => setLocationId(event.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm">
                <option value="">Any location in scope</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </select>
              <select value={providerId} onChange={(event) => setProviderId(event.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm">
                <option value="">All doctors/providers</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>{provider.name}</option>
                ))}
              </select>
              <select value={appointmentTypeId} onChange={(event) => setAppointmentTypeId(event.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm">
                <option value="">All services</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>{service.name}</option>
                ))}
              </select>
              <select value={scope} onChange={(event) => setScope(event.target.value as typeof scope)} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm">
                <option value="default">Only my assigned locations</option>
                <option value="all">All accessible locations</option>
              </select>
              <button type="button" onClick={() => void load()} className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50">
                <RotateCcw size={16} />
                Refresh
              </button>
            </div>
          </div>
        </section>

        {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
        {loading ? <div className="rounded-xl border border-slate-200 bg-white p-5 text-slate-500">Loading operational report...</div> : null}

        {data ? (
          <>
            <section className="grid gap-4 xl:grid-cols-[1fr_380px]">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-950">Operational snapshot</p>
                    <p className="mt-1 text-sm text-slate-500">Showing operational data from {rangeLabel}.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {activeFilterChips.map((chip) => (
                      <span key={chip} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <MetricCard icon={<CalendarCheck2 size={18} />} label="Appointments" value={formatNumber(appointments)} detail={`${formatPercent(completionRate)} completed`} tone="blue" />
                  <MetricCard icon={<ReceiptText size={18} />} label="Completed appointments" value={formatNumber(completedAppointments)} detail={`${formatPercent(invoiceCaptureRate)} invoice capture`} tone="emerald" />
                  <MetricCard icon={<AlertTriangle size={18} />} label="No-shows" value={formatNumber(noShows)} detail={`${formatPercent(noShowRate)} of bookings`} tone={noShowRate >= 10 ? 'amber' : 'slate'} />
                  <MetricCard icon={<TrendingDown size={18} />} label="Cancellations" value={formatNumber(cancellations)} detail={`${formatPercent(cancellationRate)} of bookings`} tone={cancellationRate >= 12 ? 'rose' : 'slate'} />
                  {financialSummary ? (
                    <>
                      <MetricCard icon={<WalletCards size={18} />} label="Revenue" value={formatCurrency(financialSummary.revenue)} detail={`${formatNumber(financialSummary.invoiceCount)} invoices`} tone="emerald" />
                      <MetricCard icon={<ReceiptText size={18} />} label="Average invoice" value={formatCurrency(financialSummary.averageTicket)} detail="Average invoice value" tone="slate" />
                    </>
                  ) : null}
                  <MetricCard icon={<Package size={18} />} label="Package redemptions" value={formatNumber(summary.packageRedemptions)} detail="Prepaid visits consumed" tone="violet" />
                  <MetricCard icon={<Users size={18} />} label="Returning patients" value={formatNumber(summary.repeatPatients)} detail="More than one visit" tone="slate" />
                  <MetricCard icon={<Package size={18} />} label="Low stock items" value={formatNumber(summary.lowStockItems)} detail="Below reorder point" tone={Number(summary.lowStockItems || 0) > 0 ? 'amber' : 'slate'} />
                  <MetricCard icon={<ClipboardList size={18} />} label="Invoices created" value={formatNumber(summary.salesRegistered)} detail="Billing records opened" tone="slate" />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-slate-500" />
                  <h2 className="text-sm font-semibold text-slate-950">What needs attention</h2>
                </div>
                <div className="mt-4 space-y-3">
                  {insights.map((insight) => (
                    <InsightRow key={insight.title} {...insight} />
                  ))}
                </div>
              </div>
            </section>

            <section className="grid gap-4 xl:grid-cols-2">
              <ChartCard title="Visit outcomes">
                <HighchartsReact highcharts={Highcharts} options={appointmentOutcomeOptions} containerProps={{ style: { height: '100%' } }} />
              </ChartCard>
              <ChartCard title="Services delivered or booked">
                <HighchartsReact highcharts={Highcharts} options={topServiceDemandOptions} containerProps={{ style: { height: '100%' } }} />
              </ChartCard>
              <ChartCard title="Booked appointment types">
                <HighchartsReact highcharts={Highcharts} options={appointmentMixOptions} containerProps={{ style: { height: '100%' } }} />
              </ChartCard>
              <ChartCard title={financialSummary ? 'Top revenue services' : 'Stock receipts vs usage'}>
                <HighchartsReact highcharts={Highcharts} options={revenueByServiceOptions} containerProps={{ style: { height: '100%' } }} />
              </ChartCard>
            </section>

            <section className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
              <Panel
                title="Top services"
                subtitle="Most recorded services in the selected range."
                rows={data.topServices.map((row) => [row.serviceName, row.count])}
                summaryLabel="Shown count"
                summaryValue={data.topServices.reduce((sum, row) => sum + row.count, 0)}
              />
              <Panel
                title="Lowest-volume services"
                subtitle="Services with the fewest recorded visits in the selected range."
                rows={data.leastUsedServices.map((row) => ({
                  label: row.serviceName,
                  value: row.count,
                  meta: row.lastUsedAt ? formatShortDate(row.lastUsedAt) : 'Date unavailable',
                }))}
                summaryLabel="Shown count"
                summaryValue={data.leastUsedServices.reduce((sum, row) => sum + row.count, 0)}
              />
              <Panel
                title="Top revenue services"
                subtitle="Highest grossing services in the selected range."
                rows={data.topRevenueServices.map((row) => [row.serviceName, row.revenue])}
                valueType="currency"
                summaryLabel="Shown revenue"
                summaryValue={data.topRevenueServices.reduce((sum, row) => sum + row.revenue, 0)}
              />
              <Panel
                title="Appointment types"
                subtitle="Booked appointment volume by scheduled service."
                rows={data.appointmentTypeCounts.map((row) => [row.appointmentTypeName, row.count])}
                summaryLabel="Shown bookings"
                summaryValue={data.appointmentTypeCounts.reduce((sum, row) => sum + row.count, 0)}
              />
              <Panel
                title="Top inventory items used"
                subtitle="Most consumed inventory linked to completed visits, normalized to each product's base UOM."
                rows={data.topInventoryItems.map((row) => ({
                  label: row.itemName,
                  value: row.quantity,
                  displayValue: formatInventoryMeasurement(row.quantity, row.unitLabel || 'unit'),
                }))}
                summaryLabel="Shown items"
                summaryValue={data.topInventoryItems.length}
              />
              <Panel
                title="Lowest-usage inventory items"
                subtitle="Inventory items with the smallest usage in the selected range, normalized to base UOM."
                rows={data.leastUsedInventoryItems.map((row) => ({
                  label: row.itemName,
                  value: row.quantity,
                  displayValue: formatInventoryMeasurement(row.quantity, row.unitLabel || 'unit'),
                  meta: row.lastUsedAt ? formatShortDate(row.lastUsedAt) : 'Date unavailable',
                }))}
                summaryLabel="Shown items"
                summaryValue={data.leastUsedInventoryItems.length}
              />
            </section>

            <section className="grid gap-4 xl:grid-cols-[1fr_1fr]">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><BarChart3 size={18} /> Stock receipt versus usage</div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">Shown by unit of measure so receipts and consumption are not mixed into one ambiguous total.</p>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <StockTile label="Receipts" rows={stockReceiptVsUsage.receiptsByUnit || []} fallbackValue={data.stockReceiptVsUsage.receipts} />
                  <StockTile label="Usage" rows={stockReceiptVsUsage.usageByUnit || []} fallbackValue={data.stockReceiptVsUsage.usage} />
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900">Report library</h2>
                    <p className="mt-1 text-sm text-slate-500">Quick access to operational report families.</p>
                  </div>
                  <select value={reportCategory} onChange={(event) => setReportCategory(event.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm">
                    {['All reports', 'Operations', 'Clients', 'Leads', 'Financials', 'Appointments', 'Stock', 'Team'].map((option) => <option key={option}>{option}</option>)}
                  </select>
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {[
                    ['Room utilization', 'Room usage and occupancy gaps.'],
                    ['Monthly sales / Tax VAT', 'Sales, corrections audit, tax, discounts, line items.'],
                    ['Clients', 'Balances, retention, new clients, top referrers.'],
                    ['Leads', 'Source, open leads, conversion, pipeline performance.'],
                    ['Revenue', 'Package sales, voucher balance, product performance.'],
                    ['Appointments', 'Schedule, employee, cancellation, no-show reports.'],
                    ['Stock', 'Movement, consumption, expiry, purchasing, suppliers.'],
                    ['Custom fields', 'Configured client, appointment, team, treatment fields.'],
                  ].map(([title, detail]) => (
                    <ReportTile key={title} title={title} detail={detail} />
                  ))}
                </div>
              </div>
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  detail,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
  tone: 'blue' | 'emerald' | 'amber' | 'rose' | 'violet' | 'slate';
}) {
  const toneStyles = {
    blue: 'bg-sky-50 text-sky-700 ring-sky-100',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    amber: 'bg-amber-50 text-amber-700 ring-amber-100',
    rose: 'bg-rose-50 text-rose-700 ring-rose-100',
    violet: 'bg-violet-50 text-violet-700 ring-violet-100',
    slate: 'bg-slate-50 text-slate-600 ring-slate-100',
  } as const;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">{value}</p>
        </div>
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ring-1 ${toneStyles[tone]}`}>{icon}</span>
      </div>
      <p className="mt-2 text-xs font-medium text-slate-500">{detail}</p>
    </div>
  );
}

function InsightRow({ title, detail, tone, icon }: { title: string; detail: string; tone: InsightTone; icon: ReactNode }) {
  const toneStyles = {
    good: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    watch: 'border-amber-200 bg-amber-50 text-amber-700',
    risk: 'border-rose-200 bg-rose-50 text-rose-700',
    neutral: 'border-slate-200 bg-slate-50 text-slate-600',
  } as const;
  return (
    <div className={`rounded-xl border p-3 ${toneStyles[tone]}`}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0">{icon}</span>
        <div>
          <p className="text-sm font-semibold text-slate-950">{title}</p>
          <p className="mt-1 text-xs leading-5 text-slate-600">{detail}</p>
        </div>
      </div>
    </div>
  );
}

function Panel({
  title,
  subtitle,
  rows,
  valueType = 'number',
  summaryLabel,
  summaryValue,
}: {
  title: string;
  subtitle?: string;
  rows: Array<[string, number]> | Array<{ label: string; value: number; meta?: string; displayValue?: string }>;
  valueType?: 'number' | 'currency';
  summaryLabel?: string;
  summaryValue?: number;
}) {
  const normalizedRows = rows.map((row) => Array.isArray(row) ? { label: row[0], value: row[1] } : row);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {subtitle ? <p className="mt-1 text-xs leading-5 text-slate-500">{subtitle}</p> : null}
        </div>
        {summaryLabel && typeof summaryValue === 'number' ? (
          <div className="inline-flex shrink-0 self-start whitespace-nowrap rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 sm:self-auto">
            {summaryLabel}: {valueType === 'currency' ? formatCurrency(summaryValue) : formatNumber(summaryValue)}
          </div>
        ) : null}
      </div>
      <div className="mt-3 divide-y divide-slate-100">
        {normalizedRows.length ? normalizedRows.map(({ label, value, meta, displayValue }) => (
          <div key={label} className="flex items-start justify-between gap-4 py-2 text-sm">
            <div className="min-w-0">
              <span className="block truncate text-slate-700" title={label}>{label}</span>
              {meta ? <span className="mt-0.5 block text-xs font-medium text-slate-500">Last used: {meta}</span> : null}
            </div>
            <span className="shrink-0 font-medium text-slate-900">{displayValue || (valueType === 'currency' ? formatCurrency(Number(value || 0)) : formatNumber(value))}</span>
          </div>
        )) : <div className="py-3 text-sm text-slate-500">No rows for this range.</div>}
      </div>
    </section>
  );
}

function ReportTile({ title, detail }: { title: string; detail: string }) {
  return (
    <button type="button" className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-left transition hover:border-slate-300 hover:bg-white">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <BarChart3 size={15} className="text-sky-600" />
        {title}
      </div>
      <p className="mt-1 text-xs leading-5 text-slate-500">{detail}</p>
    </button>
  );
}

function StockTile({
  label,
  rows,
  fallbackValue,
}: {
  label: string;
  rows: Array<{ unitLabel: string; quantity: number }>;
  fallbackValue: number;
}) {
  const visibleRows = rows.filter((row) => Number(row.quantity || 0) > 0);
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      {visibleRows.length ? (
        <div className="mt-3 space-y-2">
          {visibleRows.map((row) => (
            <div key={`${label}-${row.unitLabel}`} className="flex items-baseline justify-between gap-3 text-sm">
              <span className="text-slate-500">{row.unitLabel}</span>
              <span className="font-semibold text-slate-950">{formatNumber(row.quantity)}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-2xl font-semibold text-slate-950">{formatNumber(fallbackValue)}</p>
      )}
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="card flex min-h-[360px] flex-col overflow-hidden p-4">
      <div className="text-sm font-semibold text-ink">{title}</div>
      <div className="mt-3 min-h-[280px] flex-1">{children}</div>
    </section>
  );
}
